// Copyright (c) Docugami, Inc. All rights reserved.

import { PayloadAction, createSlice, isAnyOf } from "@reduxjs/toolkit";
import { FolderId, createFolderId, createFolderIdString } from "components/file_management/file_helpers";
import { TokenRange } from "networking/api";
import { ChunkSelectedSchema, TelemetryEvent } from "telemetry/TelemetryEvents";
import { Telemetry } from "telemetry/TelemetryManager";
import { StorageAccessor, StorageKey } from "utils/StorageAccessor";
import { TokenRangeUtils } from "utils/TokenRangeUtils";
import { startAppListening } from "./listenerMiddleware";
import { reportsApiSlice, selectEntryById } from "./reports/reportsSlice";

export interface SdvSliceState {
    document?: DocumentState;
    recentLabels?: Record<string, string[]>;
}

export interface DocumentState {
    folderId: string;
    docsetId: string;
    fileId: string;
    selection?: SelectionState;
    edit?: EditOperationState;
    currentPageOnScreen?: number;
    currentSelectedPage?: number;
    previewChunkSets: { [id: string]: PreviewChunks };
    isExpandRepeatsEnabled?: boolean
}

interface PreviewChunks {
    spanIds: string[];
    color: string;
}

export enum SelectionModifier {
    Column = "Column"
}

export interface SelectionState {
    id: string;
    defaultLabel?: string;
    selectionModifier?: SelectionModifier;
}

export interface EditOperationState {
    tokenRange: TokenRange;
}

export const sdvSlice = createSlice({
    name: 'sdv',
    initialState: {
    } as SdvSliceState,
    reducers: {
        setCurrentDocument(state, action: PayloadAction<{ docsetId: string, fileId: string } | string | undefined>) {
            const changed = state.document?.folderId !== action.payload;
            if (!action.payload) {
                delete state.document;
            }
            else {
                let folderId: FolderId;
                if (typeof action.payload === 'string') {
                    folderId = createFolderId(action.payload);
                }
                else {
                    folderId = {
                        clusterId: action.payload.docsetId,
                        fileId: action.payload.fileId,
                        value: createFolderIdString(action.payload.docsetId, action.payload.fileId)
                    }
                }

                state.document = {
                    docsetId: folderId.clusterId,
                    fileId: folderId.fileId,
                    folderId: folderId.value,
                    previewChunkSets: {}
                }
            }

            if (changed && !!state.document) {
                delete state.document.selection;
                delete state.document?.edit;
            }
        },
        setSelection(state, action: PayloadAction<SelectionState | undefined>) {
            if (!state.document) {
                return;
            }

            if (!action.payload) {
                delete state.document.selection;
                delete state.document.edit;
            }
            else {
                state.document.selection = {
                    ...(state.document.selection || {}),
                    ...action.payload
                }
            }
        },
        setSelectedId(state, action: PayloadAction<string | undefined>) {
            if (!state.document) {
                return;
            }

            if (!action.payload) {
                delete state.document.selection;
                delete state.document.edit;
            }
            else {
                state.document.selection = state.document.selection || { id: action.payload };
                state.document.selection.id = action.payload;
            }
        },
        setSelectionModifier(state, action: PayloadAction<SelectionModifier | undefined>) {
            if (!state.document?.selection) {
                return;
            }

            if (!action.payload) {
                delete state.document.selection.selectionModifier;
            }
            else {
                state.document.selection.selectionModifier = action.payload;
            }
        },
        setEditOperation(state, action: PayloadAction<TokenRange | undefined>) {
            if (!state.document) {
                return;
            }

            if (!action.payload) {
                // Only delete if edit exists - prevents infinite loops from repeated clears
                if (state.document.edit) {
                    delete state.document.edit;
                }
            }
            else if (!state.document.edit?.tokenRange || !TokenRangeUtils.rangesEqual(state.document.edit.tokenRange, action.payload)) {
                state.document.edit = {
                    tokenRange: action.payload
                }
            }
        },
        setSelectedDefaultLabel(state, action: PayloadAction<string | undefined>) {
            if (!state.document?.selection) {
                return;
            }

            if (!action.payload) {
                delete state.document.selection.defaultLabel;
            }
            else {
                state.document.selection.defaultLabel = action.payload;
            }
        },
        addRecentLabel(state, action: PayloadAction<string>) {
            const docsetId = state.document?.docsetId;
            if (!docsetId) {
                return;
            }

            let recentLabels = state.recentLabels || {};
            let docsetLabels = recentLabels[docsetId] || [];
            docsetLabels = [action.payload, ...docsetLabels.filter(x => x.toLowerCase() !== action.payload.toLowerCase())];

            if (docsetLabels.length > 10) {
                docsetLabels = docsetLabels.slice(0, 9);
            }

            recentLabels[docsetId] = docsetLabels;
            state.recentLabels = recentLabels;
        },
        setRecentLabels(state, action: PayloadAction<Record<string, string[]> | undefined>) {
            if (!action.payload) {
                delete state.recentLabels;
            }
            else {
                state.recentLabels = action.payload;
            }
        },
        setCurrentPageOnScreen(state, action: PayloadAction<number>) {
            if (!state.document) {
                return;
            }

            state.document.currentPageOnScreen = action.payload;
        },
        setCurrentSelectedPage(state, action: PayloadAction<number>) {
            if (!state.document) {
                return;
            }

            state.document.currentSelectedPage = action.payload;
        },
        setPreviewChunks(state, action: PayloadAction<{ id: string, chunks: PreviewChunks }>) {
            if (!state.document) {
                return;
            }

            state.document.previewChunkSets[action.payload.id] = action.payload.chunks;
        },
        removePreviewChunkSet(state, action: PayloadAction<string>) {
            if (!state.document) {
                return;
            }

            delete state.document.previewChunkSets?.[action.payload];
        },
        setPreviewChunkSetColor(state, action: PayloadAction<{ id: string, color: string }>) {
            if (!state.document) {
                return;
            }

            if (!!state.document.previewChunkSets?.[action.payload.id]) {
                state.document.previewChunkSets[action.payload.id] = {
                    ...state.document.previewChunkSets[action.payload.id],
                    color: action.payload.color
                }
            }
        },
        setExpandRepeatsEnabled(state, action: PayloadAction<boolean>) {
            if (!state.document) {
                return;
            }

            state.document.isExpandRepeatsEnabled = action.payload;
        }
    }
});

startAppListening({
    actionCreator: sdvSlice.actions.setCurrentDocument,
    effect: (input, listenerApi) => {
        const newState = listenerApi.getState().sdv;
        const newDocsetId = newState.document?.docsetId;
        if (!newDocsetId) {
            listenerApi.dispatch(sdvSlice.actions.setRecentLabels());
        }
        else if (!newState.recentLabels) {
            const accessor = new StorageAccessor()
            const storage = accessor.getObject<Record<string, string[]>>(StorageKey.RecentLabels, {})!;
            listenerApi.dispatch(sdvSlice.actions.setRecentLabels(storage));
        }
    }
});

// When binding to an entry or creating a new one - add entry title to recent labels
startAppListening({
    matcher: isAnyOf(reportsApiSlice.endpoints.bindEntry.matchFulfilled, reportsApiSlice.endpoints.addEntry.matchFulfilled),
    effect: (input, listenerApi) => {
        const reportId = input.meta.arg.originalArgs.reportId;
        const schemaState = reportsApiSlice.endpoints.getReportSchema.select(reportId)(listenerApi.getState())?.data;
        if (!schemaState) {
            return;
        }

        const entry = selectEntryById(schemaState.entries, input.payload.entryId);
        if (!entry) {
            return;
        }

        const docsetId = schemaState.report.clusterId;
        const currentDocsetId = listenerApi.getState().sdv.document?.docsetId;
        const label = entry.title;
        if (!!label && currentDocsetId === docsetId) {
            listenerApi.dispatch(sdvSlice.actions.addRecentLabel(label));
        }
    }
});

startAppListening({
    actionCreator: sdvSlice.actions.addRecentLabel,
    effect: (input, listenerApi) => {
        const recentLabels = listenerApi.getState().sdv.recentLabels;
        if (!!recentLabels) {
            const accessor = new StorageAccessor();
            accessor.setObject(StorageKey.RecentLabels, recentLabels);
        }
    }
});

startAppListening({
    matcher: isAnyOf(sdvSlice.actions.setSelection, sdvSlice.actions.setSelectedId),
    effect: (input, listenerApi) => {
        const oldId = listenerApi.getOriginalState().sdv.document?.selection?.id;
        const newId = listenerApi.getState().sdv.document?.selection?.id;
        if (oldId !== newId) {
            Telemetry.trackEvent<ChunkSelectedSchema>({
                spanId: newId ?? '',
                eventName: TelemetryEvent.ChunkSelected
            });
        }
    }
})

export const {
    setCurrentDocument,
    setEditOperation,
    setSelectedDefaultLabel,
    setSelection,
    setSelectedId,
    setSelectionModifier,
    setCurrentPageOnScreen,
    setCurrentSelectedPage,
    setPreviewChunks,
    removePreviewChunkSet,
    setPreviewChunkSetColor,
    setExpandRepeatsEnabled
} = sdvSlice.actions;
